/**
 * ConfidenceBadge - Visual confidence indicator for AI-extracted fields
 * Shows a colored dot + optional label to indicate extraction reliability
 */
export function ConfidenceBadge({ level, showLabel = false, size = "sm" }) {
  if (!level) return null;
  
  const config = {
    high: { 
      color: "bg-emerald-500", 
      ring: "ring-emerald-200", 
      label: "Alta",
      title: "Confidenza alta - Dato estratto e validato"
    },
    medium: { 
      color: "bg-amber-500", 
      ring: "ring-amber-200", 
      label: "Media",
      title: "Confidenza media - Verificare il dato"
    },
    low: { 
      color: "bg-red-500", 
      ring: "ring-red-200", 
      label: "Bassa",
      title: "Confidenza bassa - Dato mancante o non valido"
    }
  };
  
  const c = config[level] || config.low;
  const dotSize = size === "sm" ? "h-2 w-2" : "h-2.5 w-2.5";
  
  return (
    <span 
      className="inline-flex items-center gap-1" 
      title={c.title}
      data-testid={`confidence-badge-${level}`}
    >
      <span className={`${dotSize} rounded-full ${c.color} ring-2 ${c.ring} shrink-0`} />
      {showLabel && (
        <span className={`text-[10px] font-medium ${
          level === "high" ? "text-emerald-700 dark:text-emerald-400" : 
          level === "medium" ? "text-amber-700 dark:text-amber-400" : 
          "text-red-700 dark:text-red-400"
        }`}>
          {c.label}
        </span>
      )}
    </span>
  );
}

/**
 * ConfidenceBar - Summary bar showing overall extraction confidence
 * Displays a horizontal bar with colored segments
 */
export function ConfidenceBar({ fieldConfidence = {} }) {
  const values = Object.values(fieldConfidence);
  if (values.length === 0) return null;
  
  const counts = { high: 0, medium: 0, low: 0 };
  values.forEach(v => { if (counts[v] !== undefined) counts[v]++; });
  const total = values.length;
  
  const highPct = Math.round((counts.high / total) * 100);
  const medPct = Math.round((counts.medium / total) * 100);
  const lowPct = Math.round((counts.low / total) * 100);
  
  // Overall score: high=100, medium=50, low=0
  const score = Math.round(((counts.high * 100) + (counts.medium * 50)) / total);
  
  return (
    <div className="space-y-1.5" data-testid="confidence-bar">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground font-medium">Affidabilità AI</span>
        <span className={`font-bold ${
          score >= 75 ? "text-emerald-600" : score >= 45 ? "text-amber-600" : "text-red-600"
        }`}>
          {score}%
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden flex">
        {highPct > 0 && (
          <div 
            className="h-full bg-emerald-500 transition-all duration-500" 
            style={{ width: `${highPct}%` }} 
          />
        )}
        {medPct > 0 && (
          <div 
            className="h-full bg-amber-500 transition-all duration-500" 
            style={{ width: `${medPct}%` }} 
          />
        )}
        {lowPct > 0 && (
          <div 
            className="h-full bg-red-500 transition-all duration-500" 
            style={{ width: `${lowPct}%` }} 
          />
        )}
      </div>
      <div className="flex gap-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {counts.high} certi
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          {counts.medium} da verificare
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
          {counts.low} mancanti
        </span>
      </div>
    </div>
  );
}
